import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardBody } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { customerAPI } from '../../services/api';
import { customerFormConfig } from '../../config';

const createInitialState = (config) => {
  const state = {};
  for (const key in config.fields) {
    state[key] = config.fields[key].defaultValue || '';
  }
  return state;
};

export default function CustomerForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [formData, setFormData] = useState(createInitialState(customerFormConfig));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // IDI-247 guard (Portica paste 75): Save must be unreachable until
  // loadCustomer resolves. Without this guard, a user landing on
  // /customers/:id/edit and clicking Save before the fetch returns would
  // send an unhydrated (empty) serviceAddress over the record's real one.
  // Backend merge is top-level (cmd/customers/main.go:568-574), so an
  // empty serviceAddress payload REPLACES the stored object as a whole.
  // Only "editing" mode needs this guard — create mode starts with empty
  // fields legitimately.
  const [isLoading, setIsLoading] = useState(Boolean(isEditing && id));
  // IDI-247 Guard 2 (Portica paste 76): hydratedRef flips true ONLY inside
  // loadCustomer's success path — the property we actually mean is "did the
  // hydration land?" not "is at least one field non-empty?" (the proxy).
  // Proxies drift; this one drifted on the `country: 'US'` form default,
  // which is not customer data but voted TRUE on the emptiness test and
  // made Guard 2 inert. hydratedRef closes that gap AND covers the case
  // Guard 1's finally deliberately opens: on a 401/500 the form unlocks
  // (correctly, so it's not permanently dead), but hydratedRef stays false
  // so a submit from that state does NOT emit serviceAddress and preserves
  // the stored record. Create mode: hydratedRef stays false; that's fine,
  // create-mode submits build a new record and serviceAddress-omit is safe
  // there because there's nothing to preserve.
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (isEditing && id) {
      loadCustomer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, id]);

  const loadCustomer = async () => {
    // IDI-247 guard (Portica paste 75): isLoading starts true when editing;
    // flip to false after this fetch resolves or errors. The Save button is
    // disabled while isLoading, preventing the transient-write hole.
    try {
      const response = await customerAPI.getCustomer(id);
      const data = response.data || response;
      // IDI-245 (2026-09-03): third consumer of the IDI-48 defect class.
      // Hydration reads from `data.address.{street|city|state|zip}` — the
      // wire's DERIVED read shape (synthesised at cmd/customers/main.go:373-376
      // from serviceAddress). Portica measured this on 12/12 detail fetches.
      //
      // Three-branch mapping, same as GoldenCustomerCard.js:72-99:
      //   1. data.address.street (canonical migrated shape)
      //   2. data.address.line1  (legacy shape)
      //   3. data.serviceAddress.address1 (secondary shape / round-trip safety)
      // Form field names differ from the drawer's — this form uses
      // `streetAddress|postalCode`; the drawer uses `line1|zip`.
      //
      // 🛑 address2 preservation (IDI-247 / paste 74): the form has no visible
      // address2 field, but real records (23 of 499 on tenant 270) carry
      // `serviceAddress.address2` like "Suite C", "#299", "Suite 100". The
      // backend's updateCustomer merge (cmd/customers/main.go:568-574) is
      // top-level only — sending serviceAddress WITHOUT address2 destroys
      // the stored value. Hydrating address2 into a hidden formData slot
      // lets us round-trip it unchanged on save. Falsifier: customer 15592
      // (TDK, "Suite C") — load → no-op save → re-read must preserve address2.
      const addr = data.address || {};
      const svc = data.serviceAddress || {};
      setFormData(prev => ({
        ...prev,
        companyName: data.company || '',
        contactPerson: data.name || '',
        phoneNumber: data.phone || '',
        emailAddress: data.email || '',
        notes: data.notes || '',
        streetAddress: addr.line1 || addr.street || svc.address1 || '',
        city: addr.city || svc.city || '',
        state: addr.state || svc.state || '',
        postalCode: addr.zip || svc.postal || '',
        country: addr.country || svc.country || '',
        // Hidden slot — not editable from the form, preserved through save.
        _preservedAddress2: svc.address2 || '',
      }));
      // Hydration succeeded — Guard 2 will emit serviceAddress from this
      // point forward. On error, hydratedRef stays false and Guard 2
      // omits serviceAddress (preserving the stored record).
      hydratedRef.current = true;
    } catch {
      setErrors({ general: 'Failed to load customer data' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    const { fields } = customerFormConfig;
    for (const key in fields) {
      if (fields[key].required && !formData[key]?.trim()) {
        newErrors[key] = `${fields[key].label} is required`;
      }
    }
    if (formData.emailAddress && !/\S+@\S+\.\S+/.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    // IDI-245 (2026-09-03, amended per Portica paste 74 / IDI-247):
    // Write to `serviceAddress` — the STORED shape — NOT to `address`.
    //
    // The wire's `address.{street,city,state,zip}` object is DERIVED on
    // read at cmd/customers/main.go:373-376 from serviceAddress. Storing
    // an `address` field on the record does nothing: the next GET
    // regenerates `address` from unchanged serviceAddress and paints
    // over the stored one. An edit written to `address` silently
    // discards.
    //
    // 🛑 address2 preservation: the backend merge at :568-574 is top-level.
    // Sending `serviceAddress: {address1, city, state, postal, country}`
    // WITHOUT address2 clobbers the stored address2 (23 of 499 records
    // on tenant 270 carry a non-empty address2). Round-trip the hidden
    // _preservedAddress2 slot to keep the acceptance fixture (customer
    // 15592 "Suite C") intact.
    //
    // 🛑 IDI-247 guard (Portica paste 75): omit serviceAddress ENTIRELY
    // when every field is empty. Restores the "absent key is safe"
    // property (backend top-level merge leaves stored serviceAddress
    // untouched). Without this, an unhydrated form submit (which the
    // isLoading gate above also blocks) OR a genuinely-blank-address
    // record would REPLACE the stored object with empty strings.
    // Trade named by Portica: this form cannot CLEAR an address to
    // blank. Correct trade — the form has no address2 field so it is
    // already not a complete address editor, and 499 records with real
    // addresses outrank an unsupported clear-to-empty gesture the
    // night before launch. Users who want to clear an address use the
    // drawer/panel (which have their own IDI-247 write-half fix pending).
    //
    // 🔎 Also folded in per paste 75:
    //   - country: no record on tenant 270 carries serviceAddress.country
    //     (0 of 8 detail reads; not in the LIST key census). Emit only
    //     when the form has a non-default value.
    //   - address2: 7232 and 34770 have NO address2 key at all. If our
    //     hydration slot is empty, don't add an empty key that wasn't
    //     there before.
    // IDI-247 Guard 2 v4 (Portica paste 76): `country` is DROPPED from
    // svcFields and from the payload. No tenant-270 record carries
    // serviceAddress.country (0 of 8 detail reads, 0 of 499 in the LIST
    // key census). It exists only as a form default ('US'), and its
    // truthiness under `.some(v => v)` made Guard 2 inert on unhydrated
    // submits — Object.fromEntries then filtered out the empty fields
    // and emitted `serviceAddress: { country: 'US' }`, which REPLACED
    // the whole stored serviceAddress and destroyed the record's real
    // address. The filter that was meant to prevent writing new keys
    // was the mechanism that deleted the old ones. Portica caught it.
    //
    // Emit serviceAddress ONLY when hydration succeeded. hydratedRef
    // is the direct property; the previous emptiness-of-fields proxy
    // drifted on the form default. On create-mode or error-state
    // (hydratedRef stays false), serviceAddress is omitted → backend
    // top-level merge preserves the stored object.
    const svcFields = {
      address1: formData.streetAddress || '',
      address2: formData._preservedAddress2 || '',
      city: formData.city || '',
      state: formData.state || '',
      postal: formData.postalCode || '',
    };
    const dataToSend = {
      name: formData.contactPerson || formData.contactName,
      company: formData.companyName || formData.dealerName,
      email: formData.emailAddress,
      phone: formData.phoneNumber,
      notes: formData.notes,
      // Only emit serviceAddress when hydration completed successfully.
      // Backend top-level merge preserves stored serviceAddress when
      // this key is absent from the payload.
      ...(hydratedRef.current ? {
        serviceAddress: Object.fromEntries(
          Object.entries(svcFields).filter(([, v]) => v !== '')
        ),
      } : {}),
    };

    try {
      if (isEditing) {
        await customerAPI.updateCustomer(id, dataToSend);
      } else {
        await customerAPI.createCustomer(dataToSend);
      }
      navigate('/customers');
    } catch {
      setErrors({ general: 'Failed to save customer' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardBody>
          {errors.general && <Alert variant="error" className="mb-4">{errors.general}</Alert>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(customerFormConfig.fields).map(([key, config]) => (
                <Input
                  key={key}
                  label={`${config.label}${config.required ? ' *' : ''}`}
                  value={formData[key] || ''}
                  onChange={(e) => handleChange(key, e.target.value)}
                  error={errors[key]}
                  required={config.required}
                />
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[color:var(--color-border)]">
              <Button variant="secondary" type="button" onClick={() => navigate('/customers')}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting} disabled={isLoading || isSubmitting}>
                {isLoading ? 'Loading customer...' : isSubmitting ? 'Saving...' : isEditing ? 'Update Customer' : 'Create Customer'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
